import { useQuery } from '@apollo/client/react'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faPlay, faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useState } from 'react'
import ReactGA from 'react-ga4'
import { LIST_SONGS_BY_ID } from '../../../api/listAlbumSongs'
import {
  useMusicPlayerStore,
  type AudioItem,
} from '../../../stores/MusicPlayerService'
import type {
  AlbumSong,
  ListAlbumSongs,
  ListAlbumsResponse,
  ListAlbumVariables,
} from '../../../types/portfolio'
import { Tooltip } from '../../Tooltip'
import '../styles.scss'

const PlayButton: React.FC<ListAlbumVariables> = ({ albumId }) => {
  const [isHovered, setIsHovered] = useState(false)
  const { addAlbumToPlaylist } = useMusicPlayerStore()

  const { loading, error, data } = useQuery<
    ListAlbumsResponse,
    { albumId: string }
  >(LIST_SONGS_BY_ID, {
    variables: { albumId },
  })

  const songCount = data?.listAlbums.data[0]?.songs?.length ?? 0

  const handleFetchData = () => {
    if (!loading && !error) {
      const album = data?.listAlbums.data[0] as ListAlbumSongs

      const songs: AudioItem[] = album.songs.map((song: AlbumSong) => {
        return {
          id: song.id,
          name: song.songTitle,
          singer: song.artist,
          cover: album.image || '',
          musicSrc: song.songFile,
        }
      })

      addAlbumToPlaylist(albumId, songs)
      ReactGA.event({
        category: 'Button Click',
        action: `User clicked to add album to playlist: ${album.title}`,
        label: 'Portfolio Section',
      })
    }
  }

  return (
    <Tooltip
      content={
        'Add ' +
        songCount.toString() +
        ' song' +
        (songCount > 1 ? 's' : '') +
        ' to playlist'
      }
      placement={'bottom'}
    >
      <button
        aria-label="Add to player button"
        className="absolute bottom-2.5 right-2.5 z-10 flex h-16 w-16 items-center justify-center rounded-lg bg-white/50 p-2 text-gray-800 shadow-lg backdrop-blur-sm duration-200 ease-in-out hover:-translate-x-0.5 hover:-translate-y-0.5 hover:scale-110 hover:cursor-pointer hover:bg-opacity-70 hover:shadow-xl active:translate-x-0 active:translate-y-0 active:scale-105"
        onClick={(event) => {
          // Prevent clicks from triggering parent navigation (e.g. Links)
          event.preventDefault()
          event.stopPropagation()
          handleFetchData()
        }}
        onMouseEnter={() => {
          setIsHovered(true)
        }}
        onMouseLeave={() => {
          setIsHovered(false)
        }}
      >
        <FontAwesomeIcon
          size="lg"
          icon={isHovered ? (faPlus as IconProp) : (faPlay as IconProp)}
          inverse
          style={{ color: '#1e293b' }}
        />
      </button>
    </Tooltip>
  )
}

export default PlayButton
